import React, { useState, useEffect, useCallback } from 'react';
import { useApp, ROLE_LABELS } from '../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Receipt, CheckCircle2, XCircle, Clock, ArrowRight, ChevronDown, ChevronUp,
  Eye, AlertTriangle, FileText, MapPin, Calendar, IndianRupee
} from 'lucide-react';
import { toast } from 'sonner';
import { InstructionGuide } from '../components/layout/InstructionGuide';
import ExpenseAnalytics from '../components/expense/ExpenseAnalytics';

const STATUS_COLORS = {
  pending: 'bg-amber-100 text-amber-700',
  in_progress: 'bg-blue-100 text-blue-700',
  approved: 'bg-emerald-100 text-emerald-700',
  partially_approved: 'bg-teal-100 text-teal-700',
  rejected: 'bg-red-100 text-red-700',
  auto_rejected: 'bg-rose-100 text-rose-700',
};

const STATUS_LABELS = {
  pending: 'Pending', in_progress: 'In Progress', approved: 'Approved',
  partially_approved: 'Partially Approved', rejected: 'Rejected',
  auto_rejected: 'Auto-Rejected (Policy)',
};

const ACTION_COLORS = {
  approved: 'text-emerald-600', approved_reduced: 'text-teal-600',
  rejected: 'text-red-600', pending: 'text-slate-400',
};

export default function ExpenseClaims() {
  const { api, currentUser, selectedBusiness, isAdmin } = useApp();
  const [claims, setClaims] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [detailClaim, setDetailClaim] = useState(null);
  const [actionDialog, setActionDialog] = useState(null);
  const [stats, setStats] = useState({});
  const [billPreview, setBillPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!currentUser) return;
    try {
      setLoading(true);
      const params = { user_id: currentUser.id, role: currentUser.role };
      if (activeTab === 'pending_approval') {
        params.view = 'approvals';
      } else if (activeTab === 'my_claims') {
        params.view = 'my_claims';
      } else if (activeTab === 'rejected') {
        params.status = 'rejected,auto_rejected';
      } else if (activeTab !== 'all') {
        params.status = activeTab;
      }
      const [claimRes, statsRes] = await Promise.all([
        api.getExpenseClaims(params),
        api.getExpenseClaimStats({ user_id: currentUser.id, role: currentUser.role }),
      ]);
      setClaims(claimRes.data.data || []);
      setStats(statsRes.data || {});
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [api, currentUser, activeTab]);

  useEffect(() => { load(); }, [load]);

  const handleAction = async (claimId, action, remarks, adjustedExpenses) => {
    try {
      await api.processExpenseClaim(claimId, {
        action,
        approver_id: currentUser.id,
        remarks,
        adjusted_expenses: adjustedExpenses || undefined,
      });
      const actionLabel = action === 'reject' ? 'rejected' : action === 'approve_reduced' ? 'approved with adjustments' : 'approved';
      toast.success(`Claim ${actionLabel} successfully`);
      setActionDialog(null);
      setDetailClaim(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    }
  };

  const pendingCount = (stats.pending?.count || 0) + (stats.in_progress?.count || 0);
  const approvedCount = (stats.approved?.count || 0) + (stats.partially_approved?.count || 0);
  const rejectedCount = (stats.rejected?.count || 0) + (stats.auto_rejected?.count || 0);

  return (
    <div data-testid="expense-claims-page" className="space-y-6 fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Expense Claims</h1>
          <p className="text-sm text-slate-500 mt-1">Review and approve expense claims from travel plans</p>
        </div>
      </div>

      <InstructionGuide
        title="How Expense Claims work"
        description="When a team member uploads expense bills on a completed travel plan, a claim is auto-created and sent through the hierarchy for approval."
        steps={[
          'Claims are auto-created when expense bills are uploaded to a completed travel plan.',
          'The claim goes through every level in the hierarchy (direct manager -> up to admin).',
          'Each approver can: Approve, Approve with reduced amount (edit line items), or Reject.',
          'The claim must pass through ALL levels before receiving final approval from Admin.',
        ]}
        flow={['Bills Uploaded', 'Claim Created', 'Manager Reviews', 'Next Level Reviews', '...', 'Admin Final Approval']}
      />

      {/* Expense Trend Dashboard */}
      <ExpenseAnalytics />

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border border-border/60">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center"><Clock className="w-5 h-5 text-amber-600" /></div>
              <div><p className="text-2xl font-bold text-slate-900">{pendingCount}</p><p className="text-xs text-slate-400">Pending</p></div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-border/60">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center"><CheckCircle2 className="w-5 h-5 text-emerald-600" /></div>
              <div><p className="text-2xl font-bold text-slate-900">{approvedCount}</p><p className="text-xs text-slate-400">Approved</p></div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-border/60">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center"><XCircle className="w-5 h-5 text-red-600" /></div>
              <div><p className="text-2xl font-bold text-slate-900">{rejectedCount}</p><p className="text-xs text-slate-400">Rejected</p></div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-border/60">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center"><IndianRupee className="w-5 h-5 text-blue-600" /></div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {((stats.approved?.total_amount || 0) + (stats.partially_approved?.total_amount || 0)).toLocaleString()}
                </p>
                <p className="text-xs text-slate-400">Total Approved</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100">
          <TabsTrigger value="all" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white">All Claims</TabsTrigger>
          <TabsTrigger value="pending_approval" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white">Pending My Approval</TabsTrigger>
          <TabsTrigger value="my_claims" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white">My Claims</TabsTrigger>
          <TabsTrigger value="approved" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white">Approved</TabsTrigger>
          <TabsTrigger value="rejected" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white">Rejected</TabsTrigger>
        </TabsList>

        {['all', 'pending_approval', 'my_claims', 'approved', 'rejected'].map(tab => (
          <TabsContent key={tab} value={tab}>
            {claims.length === 0 ? (
              <Card className="border border-border/60">
                <CardContent className="p-12 text-center">
                  <Receipt className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-sm text-slate-400">
                    {tab === 'pending_approval' ? 'No claims pending your approval' : 'No expense claims found'}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {claims.map(claim => (
                  <ClaimRow key={claim.id} claim={claim} currentUser={currentUser} onView={() => setDetailClaim(claim)} />
                ))}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>

      {/* Claim Detail Dialog */}
      {detailClaim && (
        <ClaimDetailDialog
          claim={detailClaim}
          currentUser={currentUser}
          onClose={() => setDetailClaim(null)}
          onAction={(claim) => setActionDialog(claim)}
          onBillPreview={(url) => setBillPreview(url)}
        />
      )}

      {/* Action Dialog */}
      {actionDialog && (
        <ActionDialog
          claim={actionDialog}
          onClose={() => setActionDialog(null)}
          onSubmit={handleAction}
        />
      )}

      {/* Bill Preview Dialog */}
      <Dialog open={!!billPreview} onOpenChange={() => setBillPreview(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>Expense Bill</DialogTitle></DialogHeader>
          {billPreview && <img src={decodeURIComponent(billPreview)} alt="Expense bill" className="w-full rounded-lg" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ClaimRow({ claim, currentUser, onView }) {
  const isMyApproval = claim.approval_chain?.[claim.current_step]?.approver_id === currentUser?.id
    && claim.approval_chain?.[claim.current_step]?.status === 'pending';
  const chain = claim.approval_chain || [];
  const completedSteps = chain.filter(s => s.status !== 'pending').length;

  return (
    <Card
      data-testid={`claim-row-${claim.id}`}
      className={`border shadow-sm hover:shadow-md transition-all cursor-pointer ${isMyApproval ? 'border-amber-300 bg-amber-50/30' : 'border-border/60'}`}
      onClick={onView}
    >
      <CardContent className="p-4">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
            <Receipt className="w-5 h-5 text-slate-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-sm font-bold text-slate-900">{claim.user_name}</span>
              <span className="text-xs text-slate-400">({ROLE_LABELS[claim.user_role] || claim.user_role})</span>
              <Badge className={`text-[10px] ml-auto ${STATUS_COLORS[claim.status]}`}>{STATUS_LABELS[claim.status]}</Badge>
            </div>
            <div className="flex items-center gap-4 text-xs text-slate-500">
              <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {claim.plan_start_date} - {claim.plan_end_date}</span>
              <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {(claim.plan_locations || []).length} locations</span>
              <span className="flex items-center gap-1"><FileText className="w-3 h-3" /> {(claim.expense_bills || []).length} bills</span>
            </div>
            <div className="flex items-center gap-3 mt-2">
              <span className="text-xs text-slate-400">Original: <span className="font-semibold text-slate-700">Rs.{claim.original_amount?.toLocaleString()}</span></span>
              {claim.current_amount !== claim.original_amount && (
                <span className="text-xs text-slate-400">Current: <span className="font-semibold text-amber-600">Rs.{claim.current_amount?.toLocaleString()}</span></span>
              )}
              {claim.final_amount > 0 && (
                <span className="text-xs text-slate-400">Final: <span className="font-semibold text-emerald-600">Rs.{claim.final_amount?.toLocaleString()}</span></span>
              )}
              <span className="ml-auto text-xs text-slate-400">Level {completedSteps}/{chain.length}</span>
            </div>
          </div>
          {isMyApproval && (
            <div className="flex-shrink-0">
              <Badge className="bg-amber-500 text-white text-[10px] animate-pulse">Action Required</Badge>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function ClaimDetailDialog({ claim, currentUser, onClose, onAction, onBillPreview }) {
  const [showChain, setShowChain] = useState(true);
  const chain = claim.approval_chain || [];
  const currentStep = claim.current_step || 0;
  const isMyApproval = chain[currentStep]?.approver_id === currentUser?.id && chain[currentStep]?.status === 'pending';

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent data-testid="claim-detail-dialog" className="sm:max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <DialogTitle className="text-lg font-bold">Expense Claim</DialogTitle>
            <Badge className={STATUS_COLORS[claim.status]}>{STATUS_LABELS[claim.status]}</Badge>
          </div>
          <p className="text-sm text-slate-500">
            {claim.user_name} ({ROLE_LABELS[claim.user_role] || claim.user_role}) | {claim.plan_start_date} to {claim.plan_end_date}
          </p>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Amount Summary */}
          <div className="grid grid-cols-3 gap-3">
            <div className="p-3 bg-slate-50 rounded-lg text-center">
              <p className="text-xs text-slate-400">Original</p>
              <p className="text-lg font-bold text-slate-900">Rs.{claim.original_amount?.toLocaleString()}</p>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg text-center">
              <p className="text-xs text-blue-500">Current</p>
              <p className="text-lg font-bold text-blue-700">Rs.{claim.current_amount?.toLocaleString()}</p>
            </div>
            <div className={`p-3 rounded-lg text-center ${claim.final_amount > 0 ? 'bg-emerald-50' : 'bg-slate-50'}`}>
              <p className="text-xs text-slate-400">Final</p>
              <p className={`text-lg font-bold ${claim.final_amount > 0 ? 'text-emerald-700' : 'text-slate-300'}`}>
                {claim.final_amount > 0 ? `Rs.${claim.final_amount?.toLocaleString()}` : '-'}
              </p>
            </div>
          </div>

          {/* Policy Violations */}
          {(claim.policy_violations || []).length > 0 && (
            <div className="p-4 bg-rose-50 border border-rose-200 rounded-lg" data-testid="policy-violations">
              <h3 className="text-sm font-bold text-rose-800 mb-2 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" /> Policy Violations ({claim.policy_violations.length})
              </h3>
              <div className="space-y-1.5">
                {claim.policy_violations.map((v, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs text-rose-700">
                    <XCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    <span>{v.message}</span>
                  </div>
                ))}
              </div>
              {claim.status === 'auto_rejected' && (
                <p className="text-xs text-rose-600 font-semibold mt-2 pt-2 border-t border-rose-200">
                  This claim was automatically rejected due to expense policy violations.
                </p>
              )}
            </div>
          )}

          {/* Expense Line Items */}
          <div>
            <h3 className="text-sm font-bold text-slate-700 mb-2">Expense Details (Current)</h3>
            <div className="border border-border/40 rounded-lg overflow-hidden">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-slate-50 border-b">
                    <th className="p-2 text-left">Day</th>
                    <th className="p-2 text-center">Breakfast</th>
                    <th className="p-2 text-center">Lunch</th>
                    <th className="p-2 text-center">Dinner</th>
                    <th className="p-2 text-center">Night Stay</th>
                    <th className="p-2 text-center">Day Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(claim.current_expenses || []).map((e, i) => {
                    const orig = (claim.original_expenses || [])[i];
                    const dayTotal = (e.breakfast || 0) + (e.lunch || 0) + (e.dinner || 0) + (e.night_stay > 0 ? e.night_stay : 0);
                    const isReduced = orig && (
                      e.breakfast !== orig.breakfast || e.lunch !== orig.lunch ||
                      e.dinner !== orig.dinner || e.night_stay !== orig.night_stay
                    );
                    return (
                      <tr key={i} className={`border-b border-slate-50 ${isReduced ? 'bg-amber-50' : ''}`}>
                        <td className="p-2 font-semibold">Day {e.day}<br/><span className="text-slate-400 font-normal">{e.date}</span></td>
                        <td className="p-2 text-center">{e.breakfast}{orig && e.breakfast < orig.breakfast && <span className="text-red-400 text-[10px] block">was {orig.breakfast}</span>}</td>
                        <td className="p-2 text-center">{e.lunch}{orig && e.lunch < orig.lunch && <span className="text-red-400 text-[10px] block">was {orig.lunch}</span>}</td>
                        <td className="p-2 text-center">{e.dinner}{orig && e.dinner < orig.dinner && <span className="text-red-400 text-[10px] block">was {orig.dinner}</span>}</td>
                        <td className="p-2 text-center">{e.night_stay >= 0 ? e.night_stay : '-'}{orig && e.night_stay >= 0 && e.night_stay < (orig.night_stay || 0) && <span className="text-red-400 text-[10px] block">was {orig.night_stay}</span>}</td>
                        <td className="p-2 text-center font-semibold">{dayTotal}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Expense Bills */}
          {(claim.expense_bills || []).length > 0 && (
            <div>
              <h3 className="text-sm font-bold text-slate-700 mb-2">Expense Bills ({claim.expense_bills.length})</h3>
              <div className="flex flex-wrap gap-2">
                {claim.expense_bills.map((b, i) => (
                  <div key={i} className="relative group cursor-pointer" onClick={() => onBillPreview(b)}>
                    <img src={decodeURIComponent(b)} alt={`Bill ${i + 1}`} className="w-20 h-20 object-cover rounded-lg border border-border/40" />
                    <div className="absolute inset-0 bg-black/40 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <Eye className="w-5 h-5 text-white" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Approval Chain */}
          <div>
            <button className="flex items-center gap-2 text-sm font-bold text-slate-700 mb-2 w-full" onClick={() => setShowChain(!showChain)}>
              Approval Chain ({chain.length} levels)
              {showChain ? <ChevronUp className="w-4 h-4 ml-auto" /> : <ChevronDown className="w-4 h-4 ml-auto" />}
            </button>
            {showChain && (
              <div className="relative">
                {chain.map((step, i) => {
                  const isCurrent = i === currentStep && claim.status !== 'approved' && claim.status !== 'partially_approved' && claim.status !== 'rejected';
                  const isDone = step.status === 'approved';
                  const isRejected = step.status === 'rejected';
                  return (
                    <div key={i} className="flex gap-3 mb-0">
                      {/* Vertical line + dot */}
                      <div className="flex flex-col items-center">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                          isDone ? 'bg-emerald-100 text-emerald-600' :
                          isRejected ? 'bg-red-100 text-red-600' :
                          isCurrent ? 'bg-amber-100 text-amber-600 ring-2 ring-amber-300' :
                          'bg-slate-100 text-slate-400'
                        }`}>
                          {isDone ? <CheckCircle2 className="w-4 h-4" /> :
                           isRejected ? <XCircle className="w-4 h-4" /> :
                           isCurrent ? <Clock className="w-4 h-4" /> :
                           <span className="text-xs font-bold">{i + 1}</span>}
                        </div>
                        {i < chain.length - 1 && <div className={`w-0.5 h-8 ${isDone ? 'bg-emerald-300' : 'bg-slate-200'}`} />}
                      </div>
                      {/* Content */}
                      <div className={`flex-1 pb-4 ${isCurrent ? 'bg-amber-50/50 -mx-2 px-2 py-1.5 rounded-lg' : ''}`}>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-slate-900">{step.approver_name}</span>
                          <span className="text-xs text-slate-400">({step.role_label})</span>
                          {isCurrent && <Badge className="text-[10px] bg-amber-100 text-amber-700">Current</Badge>}
                        </div>
                        {step.status !== 'pending' && (
                          <div className="mt-1 text-xs">
                            <span className={`font-semibold ${ACTION_COLORS[step.action]}`}>
                              {step.action === 'approved' ? 'Approved' :
                               step.action === 'approved_reduced' ? 'Approved (Reduced)' :
                               step.action === 'rejected' ? 'Rejected' : step.action}
                            </span>
                            {step.amount_approved > 0 && (
                              <span className="text-slate-500 ml-2">
                                Rs.{step.amount_received?.toLocaleString()} → Rs.{step.amount_approved?.toLocaleString()}
                              </span>
                            )}
                            {step.remarks && <p className="text-slate-500 mt-0.5 italic">"{step.remarks}"</p>}
                            {step.timestamp && <p className="text-slate-300 mt-0.5">{new Date(step.timestamp).toLocaleString()}</p>}
                          </div>
                        )}
                        {step.status === 'pending' && !isCurrent && (
                          <p className="text-xs text-slate-300 mt-0.5">Waiting...</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Plan Info */}
          <div className="p-3 bg-slate-50 rounded-lg text-xs text-slate-500">
            <p><span className="font-semibold text-slate-700">Plan:</span> {claim.plan_visit_type === 'client_visit' ? 'Client Visit' : 'Business Location Visit'} | {claim.plan_purpose}</p>
            <p><span className="font-semibold text-slate-700">Locations:</span> {(claim.plan_locations || []).map(l => l.place_name).join(', ')}</p>
          </div>
        </div>

        <DialogFooter>
          {isMyApproval && (
            <div className="w-full flex gap-2">
              <Button
                data-testid="approve-claim-btn"
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
                onClick={() => onAction(claim)}
              >
                <CheckCircle2 className="w-4 h-4" /> Review & Approve
              </Button>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ActionDialog({ claim, onClose, onSubmit }) {
  const [action, setAction] = useState('approve');
  const [remarks, setRemarks] = useState('');
  const [expenses, setExpenses] = useState(
    (claim.current_expenses || []).map(e => ({ ...e }))
  );

  const originalTotal = claim.current_amount || 0;
  const newTotal = expenses.reduce((s, e) =>
    s + (e.breakfast || 0) + (e.lunch || 0) + (e.dinner || 0) + (e.night_stay > 0 ? e.night_stay : 0), 0
  );
  const isReduced = newTotal < originalTotal;

  const updateExpenseItem = (idx, field, val) => {
    setExpenses(prev => prev.map((e, i) =>
      i === idx ? { ...e, [field]: Math.max(0, Number(val) || 0) } : e
    ));
  };

  const handleSubmit = () => {
    if (action === 'reject' && !remarks.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    const finalAction = action === 'approve' && isReduced ? 'approve_reduced' : action;
    onSubmit(
      claim.id,
      finalAction,
      remarks,
      finalAction === 'approve_reduced' ? expenses : null,
    );
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent data-testid="claim-action-dialog" className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold">Review Expense Claim</DialogTitle>
          <p className="text-sm text-slate-500">From {claim.user_name} | Current amount: Rs.{originalTotal.toLocaleString()}</p>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Action Selection */}
          <div className="flex gap-2">
            <Button
              variant={action === 'approve' ? 'default' : 'outline'}
              className={action === 'approve' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}
              onClick={() => setAction('approve')}
            >
              <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
            </Button>
            <Button
              variant={action === 'reject' ? 'default' : 'outline'}
              className={action === 'reject' ? 'bg-red-600 hover:bg-red-700 text-white' : ''}
              onClick={() => setAction('reject')}
            >
              <XCircle className="w-4 h-4 mr-1" /> Reject
            </Button>
          </div>

          {/* Bills Preview */}
          {(claim.expense_bills || []).length > 0 && (
            <div>
              <Label className="text-xs font-bold text-slate-700">Attached Bills ({claim.expense_bills.length})</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {claim.expense_bills.map((b, i) => (
                  <img key={i} src={decodeURIComponent(b)} alt={`Bill ${i + 1}`} className="w-16 h-16 object-cover rounded-lg border border-border/40" />
                ))}
              </div>
            </div>
          )}

          {/* Editable Expense Table (only for approve) */}
          {action === 'approve' && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs font-bold text-slate-700">Edit Expense Line Items</Label>
                {isReduced && (
                  <Badge className="bg-amber-100 text-amber-700 text-[10px]">
                    Reduced: Rs.{originalTotal.toLocaleString()} → Rs.{newTotal.toLocaleString()}
                  </Badge>
                )}
              </div>
              <div className="space-y-2">
                {expenses.map((exp, i) => (
                  <div key={i} className="p-3 border border-border/40 rounded-lg bg-white">
                    <p className="text-xs font-bold text-slate-700 mb-2">Day {exp.day} - {exp.date}</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      <div>
                        <Label className="text-[10px]">Breakfast</Label>
                        <Input type="number" min="0" value={exp.breakfast} onChange={e => updateExpenseItem(i, 'breakfast', e.target.value)} className="h-8 text-sm" />
                      </div>
                      <div>
                        <Label className="text-[10px]">Lunch</Label>
                        <Input type="number" min="0" value={exp.lunch} onChange={e => updateExpenseItem(i, 'lunch', e.target.value)} className="h-8 text-sm" />
                      </div>
                      <div>
                        <Label className="text-[10px]">Dinner</Label>
                        <Input type="number" min="0" value={exp.dinner} onChange={e => updateExpenseItem(i, 'dinner', e.target.value)} className="h-8 text-sm" />
                      </div>
                      {exp.night_stay >= 0 && (
                        <div>
                          <Label className="text-[10px]">Night Stay</Label>
                          <Input type="number" min="0" value={exp.night_stay} onChange={e => updateExpenseItem(i, 'night_stay', e.target.value)} className="h-8 text-sm" />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="text-right mt-2">
                <span className={`text-sm font-bold ${isReduced ? 'text-amber-600' : 'text-slate-900'}`}>
                  New Total: Rs.{newTotal.toLocaleString()}
                </span>
              </div>
            </div>
          )}

          {/* Remarks */}
          <div>
            <Label>Remarks {action === 'reject' && <span className="text-red-500">*</span>}</Label>
            <Textarea
              data-testid="claim-remarks"
              value={remarks}
              onChange={e => setRemarks(e.target.value)}
              placeholder={action === 'reject' ? 'Reason for rejection (required)...' : 'Optional remarks...'}
              className="mt-1"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            data-testid="submit-claim-action"
            className={action === 'reject' ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-emerald-600 hover:bg-emerald-700 text-white'}
            onClick={handleSubmit}
          >
            {action === 'reject' ? 'Reject Claim' : isReduced ? 'Approve with Reduced Amount' : 'Approve & Forward'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
